import mergeDeep from 'deepmerge';
import { call, put, select, take } from 'redux-saga/effects';
import { apiAction, get, getPathWithQueryString, mapStateToArgs, pageContext } from 'ctm-util-functions';
import qsParser from 'querystring';

import { setTokens } from './utils/tokens';
import { addCompleteAction } from './utils';

/**
 * Load users previous journey data onto the page, so they can pickup from where they left off.
 *
 * @param {Object} pageToLoad - The current page we are loading.
 * @param {Object} pageState - Tightly coupled to PageWrapper mapStateToProps(), it has all page settings.
 * @param {Object} location - Location information passed from react-router,
 * see https://github.com/ReactTraining/react-router/blob/bd147e88c9a162f740fb7f9cd4a218343603e3ee/docs/Glossary.md#locationdescriptor
 */
export const handleLoadJourney = function* (pageToLoad, pageState, location) {
    const path = location.pathname || '';
    const query = qsParser.parse(location.search.substr(1)) || {};

    // Extract (some) query parameters and redirect so the URL is much cleaner.
    let paramsToRemoveFromUrl = [];
    if (query.access_token || query.refresh_token) {
        yield call(setTokens, { access_token: query.access_token, refresh_token: query.refresh_token });
        paramsToRemoveFromUrl = paramsToRemoveFromUrl.concat(['access_token', 'refresh_token']);
    }

    // @todo: not sure why we need this, why do we want to delete token from the url?
    if (paramsToRemoveFromUrl.length) {
        const queryWithoutTokens = Object.keys(query).reduce((acc, key) => {
            if (paramsToRemoveFromUrl.find(((param) => param === key))) return acc;
            return { ...acc, [key]: query[key] };
        }, {});
        const queryStringWithoutTokens = `?${qsParser.encode(queryWithoutTokens)}`;
        const browserHistory = pageContext.getProps().browserHistory;
        yield call(browserHistory.replace, getPathWithQueryString(path, queryStringWithoutTokens));
    }

    // Get the load Journey Api settings. deep merge loadJourney api from settings and individual page
    const settings = pageContext.getProps().models.pageSchemaModel.getSettings();
    const settingApiSchema = yield call(get, settings, 'api.loadJourney', {});
    const pageApiSchema = get(pageToLoad, 'api.loadJourney', {});
    const apiSchema = mergeDeep(settingApiSchema, pageApiSchema, { arrayMerge: (dst, src) => dst.concat(src) });

    // Load the journey Data. Also add complete action so we know when the whole load journey is completed
    if (apiSchema.requestAction) {
        const restArgs = yield select(mapStateToArgs, apiSchema.restArgs);
        const action = addCompleteAction(apiAction(apiSchema, pageState, restArgs), 'LOAD_JOURNEY_COMPLETE');
        yield put(action);
    }

    // pause the generator load journey actions are all complete
    yield take('LOAD_JOURNEY_COMPLETE');

    // if load from email, save the marketing data
    if (query.tracking_source === 'email') {
        yield put({ type: 'SAVE_MARKETING' });
    }

    // if a redirectTo path is set, navigate directly without page validation
    if (apiSchema.redirectTo) {
        const browserHistory = pageContext.getProps().browserHistory;
        yield call(browserHistory.replace, apiSchema.redirectTo);
    } else if (get(apiSchema, 'progressToPage', true)) {
        // validate all previous pages, then take user to the first page with an error
        yield put({ type: 'PROGRESS_TO_PAGE', pageName: pageToLoad.name });
    }
};
